'use es6';

import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BATTLESHIP, GYPSUM, OLAF, PANTERA } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import { canReadText } from 'UIComponents/core/Color';
import UnreadableShepherd from 'rich-text-lib/components/shepherd/UnreadableShepherd';
import { path } from 'rich-text-lib/utils/iterables';
import CustomToolbarMessage from './CustomToolbarMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `tinymceThemes.unreadable.${key}`;
const UnreadableContainer = styled.div.withConfig({
  displayName: "IframeFooter__UnreadableContainer",
  componentId: "dn6vip-0"
})(["margin:5px 0 5px auto;"]);
const MessageContainer = styled.div.withConfig({
  displayName: "IframeFooter__MessageContainer",
  componentId: "dn6vip-1"
})(["margin:5px 0;"]);
const StyledHelpIcon = styled(UIHelpIcon).withConfig({
  displayName: "IframeFooter__StyledHelpIcon",
  componentId: "dn6vip-2"
})(["margin-left:10px;"]);
const StyledFooter = styled(UIFlex).withConfig({
  displayName: "IframeFooter__StyledFooter",
  componentId: "dn6vip-3"
})(["border-top:1px solid ", ";background-color:", ";min-height:48px;display:flex;padding:0 8px;"], BATTLESHIP, GYPSUM);
const getUnreadableRatio = editor => {
  const coloredNodes = editor.dom.select('span:not(.mce-token)[style*="color"]');
  const totalChars = editor.getBody().textContent.length;
  const unreadableCharCount = Array.from(coloredNodes).reduce((acc, node) => {
    const computedStyles = window.getComputedStyle(node);
    const {
      color
    } = computedStyles;
    const parentWithBackground = editor.dom.getParent(node, parentNode => {
      if (parentNode.style.backgroundColor) {
        return true;
      }
      return false;
    });
    const backgroundColor = parentWithBackground.style.backgroundColor;
    if (!canReadText(backgroundColor, color) && node.textContent) {
      return acc + node.textContent.length;
    }
    return acc;
  }, 0);
  return unreadableCharCount / totalChars;
};
export default function IframeFooter({
  editor
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [isUnreadable, setIsUnreadable] = useState(false);
  const lastIsChecked = useRef(isChecked);
  useEffect(() => {
    lastIsChecked.current = isChecked;
  }, [isChecked]);
  const {
    getUserAttributes,
    setUserAttribute
  } = path(['plugins', 'hscore', 'api'], editor);
  const enabledFeatures = editor.getParam('enabledFeatures');
  const enforceEnabledFeatures = editor.getParam('enforceEnabledFeatures');
  const showCustomToolbarMessage = !enforceEnabledFeatures && enabledFeatures && !!enabledFeatures.length;
  const handleChange = useCallback(() => {
    const body = editor.getBody();
    const checked = !lastIsChecked.current;
    if (checked) {
      body.style.backgroundColor = PANTERA;
    } else {
      body.style.backgroundColor = OLAF;
    }
    setIsChecked(checked);
  }, [editor]);
  useEffect(() => {
    const body = editor.getBody();
    const handleInitialRatio = () => {
      if (!body.style.backgroundColor) {
        body.style.backgroundColor = OLAF;
      }
      const initialRatio = getUnreadableRatio(editor);
      if (initialRatio >= 0.5) {
        setTimeout(() => {
          // Delay showing the tooltip so the editor can finish initializing
          setIsUnreadable(true);
        }, 500);
      }
    };
    const handleExecCommand = evt => {
      if (['hscolorapply', 'hscolorremove'].includes(evt.command)) {
        editor.once('nodeChange', () => {
          // Don't calculate unreadable ratio if dark editor is already on
          if (lastIsChecked.current) return;
          const ratios = getUnreadableRatio(editor);
          if (ratios >= 0.5) {
            setIsUnreadable(true);
          }
        });
      }
    };
    if (body) {
      handleInitialRatio();
    } else {
      editor.on('init', handleInitialRatio);
    }
    editor.on('ExecCommand', handleExecCommand);
    return () => {
      editor.off('ExecCommand', handleExecCommand);
    };
  }, [editor, handleChange]);
  return /*#__PURE__*/_jsxs(StyledFooter, {
    "data-test-id": "iframe-footer",
    align: "center",
    wrap: "wrap",
    children: [showCustomToolbarMessage && /*#__PURE__*/_jsx(MessageContainer, {
      children: /*#__PURE__*/_jsx(CustomToolbarMessage, {})
    }), /*#__PURE__*/_jsxs(UnreadableContainer, {
      children: [/*#__PURE__*/_jsx(UnreadableShepherd, {
        isUnreadable: isUnreadable,
        getUserAttributes: getUserAttributes,
        setUserAttribute: setUserAttribute,
        children: /*#__PURE__*/_jsx(UIButton, {
          use: "tertiary-light",
          size: "small",
          onClick: handleChange,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`button.${isChecked ? 'turnOff' : 'turnOn'}`)
          })
        })
      }), /*#__PURE__*/_jsx(StyledHelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('tooltip.body')
        }),
        tooltipPlacement: "top left",
        tooltipProps: {
          use: 'longform',
          headingText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('tooltip.header')
          })
        }
      })]
    })]
  });
}